import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import accountService from "../service-api/accountService";
import interviewRoundService from "../service-api/interviewRoundsService";
import { toast } from "react-toastify";

// Initial state
const initialState = {
  interviewedCandidates: [],
  allInterviewStatusEmail: [],
  allInterviewRounds: [],
  pastInterviews: [],
  interviewNotes: [],
  teamEvents: [],
  data: [],
  error: null,
  isLoading: false,
};

export const fetchAllInterviewRounds = createAsyncThunk(
  "data/fetchAllInterviewRounds",
  async ({ interviewer } = {}) => {
    const response = await interviewRoundService.fetchAllInterviewRounds(interviewer);
    return response.data;
  }
);

export const fetchInterviewRoundbyCandidateIDReqID = createAsyncThunk(
  "data/fetchInterviewRoundbyCandidateIDReqID",
  async ({ reqID, canID }) => {
    const response =
      await interviewRoundService.fetchInterviewRoundbyCandidateIDReqID(
        reqID,
        canID
      );
    return response.data;
  }
);

export const createInterviewRound = createAsyncThunk(
  "data/createInterviewRound",
  async (obj) => {
    const response = await interviewRoundService.createInterviewRound(obj);
    return response.data;
  }
);

export const editInterviewRound = createAsyncThunk(
  "data/editInterviewRound",
  async ({ id, obj }) => {
    const response = await interviewRoundService.editInterviewRound(id, obj);
    return response.data;
  }
);

// Interview Email Status
export const fetchAllInterviewStatusEmailSent = createAsyncThunk(
  "data/fetchAllInterviewStatusEmailSent",
  async () => {
    const response =
      await interviewRoundService.fetchAllInterviewStatusEmailSent();
    return response.data;
  }
);

export const fetchEmailByReqIDCanID = createAsyncThunk(
  "data/fetchEmailByReqIDCanID",
  async ({ reqID, canID }) => {
    const response = await interviewRoundService.fetchEmailByReqIDCanID(
      reqID,
      canID
    );
    return response.data;
  }
);

export const addInterviewStatusEmailSent = createAsyncThunk(
  "data/addInterviewStatusEmailSent",
  async ({ obj, reqID, canID }, { dispatch }) => {
    const response = await interviewRoundService.addInterviewStatusEmailSent(
      obj
    );

    await dispatch(fetchEmailByReqIDCanID({ reqID, canID }));
    return response.data;
  }
);

export const fetchAllPastInterviewsById = createAsyncThunk(
  "data/fetchAllPastInterviewsById",
  async (candidateId) => {
    const response = await interviewRoundService.getCandidatePastInterviews(
      candidateId
    );
    return response.data;
  }
);

export const addInterviewNotes = createAsyncThunk(
  "data/addInterviewNotes",
  async ({ note, candidateId, requisitionId }, { dispatch }) => {
    const response = await interviewRoundService.addInterviewNotes(note);
    dispatch(fetchInterviewNotesById({ candidateId, requisitionId }));
    return response.data;
  }
);

export const fetchInterviewNotesById = createAsyncThunk(
  "data/fetchInterviewNotesById",
  async ({ candidateId, requisitionId }) => {
    const response = await interviewRoundService.getInterviewNotes(
      candidateId,
      requisitionId
    );
    return response.data;
  }
);

export const updateNotes = createAsyncThunk(
  "data/updateNotes",
  async ({ note,candidateId, requisitionId  }, { dispatch }) => {
    const response = await interviewRoundService.updateInterviewNotes(
      note
    );
    dispatch(fetchInterviewNotesById({ candidateId, requisitionId }));
  }
);

export const fetchAllInterviewedCandidates = createAsyncThunk(
  "data/fetchAllInterviewedCandidates",
  async () => {
    const response =
      await interviewRoundService.fetchAllInterviewedCandidates();
    return response.data;
  }
);

export const fetchAllTeamEvents = createAsyncThunk(
  "data/fetchAllTeamEvents",
  async (token) => {
    const response = await interviewRoundService.fetchAllTeamEvents(token);
    return response.data;
  }
);

// Create slice
const interviewRoundSlice = createSlice({
  name: "interview",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllInterviewRounds.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAllInterviewRounds.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allInterviewRounds = action.payload.value;
      })
      .addCase(fetchAllInterviewRounds.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    builder
      .addCase(fetchInterviewRoundbyCandidateIDReqID.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        fetchInterviewRoundbyCandidateIDReqID.fulfilled,
        (state, action) => {
          state.isLoading = false;
          state.data = action.payload.value;
        }
      )
      .addCase(
        fetchInterviewRoundbyCandidateIDReqID.rejected,
        (state, action) => {
          state.isLoading = false;
          state.error = action.error.message;
        }
      );

    builder
      .addCase(createInterviewRound.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createInterviewRound.fulfilled, (state, action) => {
        state.isLoading = false;
        // state.data = action.payload.value;
      })
      .addCase(createInterviewRound.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    builder
      .addCase(editInterviewRound.pending, (state, action) => {
        const { id, obj } = action.meta.arg;

        const index = state.data.findIndex(
          (x) => x.itl_interviewrounds2id === id
        );

        if (index !== -1) {
          state.data[index] = {
            ...state.data[index],
            ...obj, // optimistic values
          };
        }
        state.isLoading = true;
      })
      .addCase(editInterviewRound.fulfilled, (state, action) => {
        state.isLoading = false;
        // toast.info("Interview Details Updated");
        // state.data = action.payload.value;
      })
      .addCase(editInterviewRound.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    // Interview Status Update Email
    builder
      .addCase(fetchAllInterviewStatusEmailSent.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAllInterviewStatusEmailSent.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allInterviewStatusEmail = action.payload.value;
      })
      .addCase(fetchAllInterviewStatusEmailSent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    builder
      .addCase(fetchEmailByReqIDCanID.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchEmailByReqIDCanID.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allInterviewStatusEmail = action.payload.value;
      })
      .addCase(fetchEmailByReqIDCanID.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    builder
      .addCase(addInterviewStatusEmailSent.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addInterviewStatusEmailSent.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(addInterviewStatusEmailSent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    builder
      .addCase(fetchAllPastInterviewsById.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAllPastInterviewsById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.pastInterviews = action.payload.value;
      })
      .addCase(fetchAllPastInterviewsById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    builder
      .addCase(fetchInterviewNotesById.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchInterviewNotesById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.interviewNotes = action.payload.value;
      })
      .addCase(fetchInterviewNotesById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    builder
      .addCase(addInterviewNotes.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addInterviewNotes.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(addInterviewNotes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });
    builder
      .addCase(fetchAllInterviewedCandidates.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAllInterviewedCandidates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.interviewedCandidates = action.payload.value;
      })
      .addCase(fetchAllInterviewedCandidates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    // Update Notes
    builder
      .addCase(updateNotes.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateNotes.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(updateNotes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    // get team events
    builder
      .addCase(fetchAllTeamEvents.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAllTeamEvents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.teamEvents = action.payload.value;
      })
      .addCase(fetchAllTeamEvents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });
  },
});

export default interviewRoundSlice.reducer;
